import React from 'react';

import { GQStatus, GQStatusChip } from '~/components/Clive/StatusChip/GQStatusChip';
import type { GeneralEmailAnalysisExtra } from '~/components/communications/GeneralEmailCommunicationAiAnalysis/generalEmailAnalysisExtra';
import { CommunicationAiAnalysisVersion } from '~/components/communications/GeneralEmailCommunicationAiAnalysis/generalEmailAnalysisExtra';
import { doesFnolCategoryExists } from '~/components/communications/GeneralEmailCommunicationAiAnalysis/GeneralEmailCommunicationAiAnalysisUtils';
import { useGQClassification } from '~/components/hooks/useGQClassification';

interface GeneralEmailCommunicationAiAnalysisColumnIndicatorProps {
  gqAnalysisExtra: GeneralEmailAnalysisExtra;
  cliveStoreInformation?: {
    processing_status?: string;
    is_processing_complete?: boolean;
    is_processing_error?: boolean;
  };
}

function getStatusChipFromGqAnalysisExtra(
  gqAnalysisExtra: GeneralEmailAnalysisExtra,
  isFnolCategoryClassificationEnabled: boolean
): GQStatus | null {
  if (gqAnalysisExtra?.is_successful === false) {
    return GQStatus.ERROR;
  }
  if (gqAnalysisExtra?.classification_response?.related_claim_recommendation) {
    return GQStatus.CLAIM_UPDATE;
  }
  if (doesFnolCategoryExists(gqAnalysisExtra) && isFnolCategoryClassificationEnabled) {
    return GQStatus.NEW_FNOL;
  }
  if (gqAnalysisExtra?.is_successful === true) {
    return GQStatus.SUCCESS;
  }

  return null;
}

export const GeneralEmailCommunicationAiAnalysisColumnIndicator: React.FC<
  GeneralEmailCommunicationAiAnalysisColumnIndicatorProps
> = ({ gqAnalysisExtra, cliveStoreInformation }) => {
  const { isFnolCategoryClassificationEnabled } = useGQClassification();
  if (
    !cliveStoreInformation &&
    (!gqAnalysisExtra ||
      !gqAnalysisExtra.classification_response ||
      gqAnalysisExtra.version !== CommunicationAiAnalysisVersion)
  ) {
    // no ai analysis or outdated version
    return null;
  }

  let chipContent: GQStatus | null = null;

  if (gqAnalysisExtra && Object.keys(gqAnalysisExtra).length > 0) {
    chipContent = getStatusChipFromGqAnalysisExtra(gqAnalysisExtra, isFnolCategoryClassificationEnabled);
  }

  // Fallback to cliveStoreInformation only if no status found from gqAnalysisExtra
  if (chipContent === null) {
    switch (cliveStoreInformation?.processing_status) {
      case 'success':
        chipContent = GQStatus.SUCCESS;
        break;
      case 'processing':
        chipContent = GQStatus.PROCESSING;
        break;
      case 'error':
        chipContent = GQStatus.ERROR;
        break;
    }
  }

  return <GQStatusChip status={chipContent} />;
};
